import { Injectable } from '@angular/core';
import { Observable, Subscription, EMPTY, of, Subject } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { SnEnvService } from '@insight/serenity-env';
import { expand } from 'rxjs/operators';
import { AutoUnsubscribe } from '../decorators/AutoUnsubscribe';
import { ManualUsageEventService, ClientBillingIgnoreProductService, AggregationResultService, ClientInfoService, ClientStatusTableService } from '../api/services';
import { ManualUsageEvent, ClientBillingIgnoreProduct, ClientBillingAggregationResult, Party, ClientMonthlyStatusTableEntity } from '../api/models';

@Injectable({
  providedIn: 'root'
})
@AutoUnsubscribe
export class SharedDataService {
  // #region Members
  public clientBillingBackupFileConfigs: any[] = [];
  public relatedClients: any[] = [];
  public manualUsage: ManualUsageEvent[] = [];
  public aviClientIds: number[] = [];
  public clientParties: Party[] = [];
  public allClientParties: Party[] = [];
  public allClientstatusEntities: ClientMonthlyStatusTableEntity[] = [];
  public aggregationResults: ClientBillingAggregationResult[] = [];
  public aggregationXmitDataClientIds: any[] = [];
  public ignoreProducts: ClientBillingIgnoreProduct[] = [];
  public updatedManualUsage = new Subject<any>();
  private stateIdx = 0;

  private _aggregationResultsSub: Subscription;
  private _ignoreProductResultsSub: Subscription;
  // #endregion

  // #region Constructor
  constructor(private _httpService: HttpService,
              private _envService: SnEnvService,
              private _manualUsageEventService: ManualUsageEventService,
              private _clientBillingIngoreProducts: ClientBillingIgnoreProductService,
              private _aggregationResultService: AggregationResultService,
              private _clientInfoService: ClientInfoService,
              private _clientStatusTableService: ClientStatusTableService ) { }
  // #endregion

  // #region Public Methods
  public async PopulateAviClientIds(force: boolean = false) {
    // quick out if we already have data and aren't forcing a reset.
    if (force) {
      this.aviClientIds = [];
      this.clientParties = [];
    } else if (this.aviClientIds.length !== 0) {
      return of();
    }

    let result = await this._clientInfoService.GetAllClientParties().toPromise();

    result = result.filter(p => Number.isInteger(parseInt(p.clientParty.mailStop, 10)));

    this.aviClientIds = result
      .map(res => parseInt(res.clientParty.mailStop, undefined))
      .sort((a, b) => (a > b ? 1 : -1 ));

    this.clientParties = result.filter(c => c.clientParty.clientStatus === "Active");
    this.allClientParties = result;
  }

  // --------------------------------------------------

  public async PopulateAggregationResults(force: boolean = false) {
    // quick out if we already have data and aren't forcing a reset.
    if (force) {
      this.aggregationResults = [];
    } else if (this.aggregationResults.length !== 0) {
      return of();
    }

    let results = await this._aggregationResultService.GetPrevMonth().toPromise();
    this.aggregationResults = results;
    return results;
  }

  // --------------------------------------------------

  public async PopulateIgnoreProducts(force: boolean = false) {
    // quick out if we already have data and aren't forcing a reset.
    if (force) {
      this.ignoreProducts = [];
    } else if (this.ignoreProducts.length !== 0) {
      return of();
    }

    let results = await this._clientBillingIngoreProducts.GetAll().toPromise();
    this.ignoreProducts = results;
    return results;
  }

  public async PopulateIncompleteExtractionClients(force: boolean = false) {
    // quick out if we already have data and aren't forcing a reset.
    if (force) {
      this.allClientstatusEntities = [];
    } else if (this.allClientstatusEntities.length !== 0) {
      return of();
    }

    let results = await this._clientStatusTableService.GetCurrentPeriodClientEntities().toPromise();
    this.allClientstatusEntities = results;
    return results;
  }

  // --------------------------------------------------
  public HandleError(error: any): void {
    alert('An error occurred. Please see console log.');
    console.log(error);
  }
  // #endregion

  // #region Private Methods
  public PopulateClientBillingBackupFileConfigurations(force: boolean = false): Observable<void> {
    if (force) {
      this.clientBillingBackupFileConfigs = [];
    } else if (this.clientBillingBackupFileConfigs.length !== 0) {
      return of();
    }

    return this.GetBackupFileConfigurations()
    .pipe(
      expand(res => {
          // check for errors
          if (!res.success) {
            this.HandleError(res.errors);
            return EMPTY;
          }
          // make sure we have data
          if (!res.data || !res.data.results) {
            return EMPTY;
          }
          // add the data (if any)
          const results = res.data.results;
          results.forEach(result => {
              if (!isNaN(result.aviClientId)) {
                this.clientBillingBackupFileConfigs.push(
                {
                  AviClientId: result.aviClientId,
                  FileType: result.fileType
                });
              }
            });
          // don't recurse if there's no continuation token.
          if (!res.data.continuationToken) {
            return EMPTY;
          }
          return this.GetBackupFileConfigurations(res.data.continuationToken);
        })
      );
    }

  GetBackupFileConfigurations(continuationToken?: string): Observable<any> {
    let url = this._envService.get('api_url') + 'backupfileconfiguration';
    if (continuationToken) {
      url += '?continuationToken=' + encodeURIComponent(continuationToken);
    }
    return this._httpService.get<any>(url);
  }

  public async PopulateManualUsageByPeriod(month: number, year: number, dataFilters?: any) {
    let state = ++this.stateIdx + 0;
    let response: ManualUsageEvent[];
    let request: ManualUsageEventService.GetAllManualUsageEventsParams;
    try {
        if (dataFilters) {
          request = { year: year, month: month, clientId: dataFilters };
        } else {
          request = { year: year, month: month};
        }
        response = await this._manualUsageEventService.GetAllManualUsageEvents(request).toPromise();
    } catch (err) {
      this.HandleError(err);
      return false;
    }
    // Make sure we're only updating listeners if this is the latest request.
    if (this.stateIdx === state) {
      this.manualUsage = response;
      this.updatedManualUsage.next(this.manualUsage);
    }
  }

  public updateManualUsage(id, manualUsageData: ManualUsageEvent) {
    let mu = this.manualUsage.find(el => el.id === id);
    Object.keys(manualUsageData).forEach(prop => {
      mu[prop] = manualUsageData[prop];
      mu.id = id;
    });
    this.manualUsage = [... this.manualUsage];
    this.updatedManualUsage.next(this.manualUsage);
  }

  public relatedClientFilterSelected(clientToFilterOut: string) {
    var fileteredClients = this.relatedClients.filter(f => f.clientId !== clientToFilterOut);
    return fileteredClients;
  }
}
